import { useState, useEffect } from 'react';
import Modal from '../../../../components/shareable/modal';
import { useDashboard } from '../../../../context/dashboardContext';

// Mirrors updateConveyorModal.js (same Modal, image display + UPLOAD, field/validation/button
// pattern) so managing a product feels identical to the Modular Conveyor page. Edits List Price,
// Our Cost, Description, and lets an admin upload/replace the product's display image. SKU is
// read-only (it's the identity). Submits as multipart FormData, like updateModularConveyor.
const UpdateCatalogModal = ({ isOpen, onClose, onSave, initialData = {} }) => {
  const [formData, setFormData] = useState({ list: '', babs_cost: '', module_description: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const { getLayoutImage } = useDashboard();

  useEffect(() => {
    if (!initialData) return;
    setFormData({
      list: initialData.list ?? '',
      babs_cost: initialData.babs_cost ?? '',
      module_description: initialData.module_description ?? '',
    });
    setErrors({});
    setNewImageFile(null);
    setNewImagePreview(null);
    setImageUrl(null);
    setImageError(false);
    const name = initialData.image_name;
    if (name) {
      setImageLoading(true);
      getLayoutImage(name)
        .then((res) => { if (res && res.success && res.url) setImageUrl(res.url); else setImageError(true); })
        .catch(() => setImageError(true))
        .finally(() => setImageLoading(false));
    }
  }, [initialData, getLayoutImage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateImageFile = (file) => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) { alert('Please select a valid image file (JPEG, PNG, GIF, WebP)'); return false; }
    if (file.size > 5 * 1024 * 1024) { alert('Image size must be less than 5MB'); return false; }
    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && validateImageFile(file)) {
      if (newImagePreview) URL.revokeObjectURL(newImagePreview);
      setNewImageFile(file);
      setNewImagePreview(URL.createObjectURL(file));
      setImageError(false);
    }
  };

  const removeNewImage = () => {
    if (newImagePreview) URL.revokeObjectURL(newImagePreview);
    setNewImageFile(null);
    setNewImagePreview(null);
  };

  const validateForm = () => {
    const e = {};
    if (formData.list === '' || formData.list === null) e.list = 'List price is required';
    else if (!Number.isFinite(Number(formData.list)) || Number(formData.list) < 0) e.list = 'Enter a valid price (≥ 0)';
    if (formData.babs_cost !== '' && (!Number.isFinite(Number(formData.babs_cost)) || Number(formData.babs_cost) < 0)) e.babs_cost = 'Enter a valid cost (≥ 0)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('list', Number(formData.list));
      if (formData.babs_cost !== '') fd.append('babs_cost', Number(formData.babs_cost));
      fd.append('module_description', formData.module_description ?? '');
      if (newImageFile) fd.append('image', newImageFile);
      await onSave(fd);
      removeNewImage();
    } finally {
      setSaving(false);
    }
  };

  const showNew = !!newImagePreview;
  const showCurrent = !showNew && imageUrl && !imageError;

  return (
    <Modal isOpen={isOpen} width="2xl" onClose={onClose}>
      <h2 className="text-lg font-semibold mb-1">Update Product</h2>
      <p className="text-sm text-gray-500 mb-4">
        Price changes apply to <span className="font-semibold">every</span> orientation and variant of this product, instantly.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4 p-2 max-h-[70vh] overflow-y-auto">
          {/* Image: current or newly-picked, with upload */}
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Image</label>
            <div className="flex flex-col items-center w-full border-2 border-dashed border-gray-300 rounded-lg p-3">
              <div className="flex items-center justify-center w-full h-40 bg-gray-50 rounded overflow-hidden mb-2">
                {imageLoading ? (
                  <div className="text-gray-500 text-sm">Loading image…</div>
                ) : showNew ? (
                  <img src={newImagePreview} alt="new" className="max-h-full max-w-full object-contain" />
                ) : showCurrent ? (
                  <img src={imageUrl} alt={initialData?.sku || 'product'} className="max-h-full max-w-full object-contain" onError={() => setImageError(true)} />
                ) : (
                  <div className="text-gray-400 text-sm">{initialData?.image_name ? 'No image available' : 'No image yet — upload one'}</div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer text-sm text-red-600 hover:text-red-700 font-medium">
                  {showNew ? 'Choose a different image' : (initialData?.image_name ? 'Upload / replace image' : 'Upload image')}
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
                {showNew && (
                  <button type="button" onClick={removeNewImage} className="text-sm text-gray-500 hover:text-gray-700">Cancel new image</button>
                )}
              </div>
              {(showNew || initialData?.image_name) && (
                <p className="mt-1 text-xs text-gray-400 text-center truncate max-w-full">
                  {showNew ? newImageFile?.name : initialData.image_name}
                </p>
              )}
            </div>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">SKU (Part No.)</label>
            <input type="text" value={initialData?.sku || ''} readOnly className="w-full p-2 border rounded bg-gray-100 text-gray-600 font-mono cursor-not-allowed" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">List Price<span className="text-red-500 ml-1">*</span></label>
            <input type="number" name="list" step="0.01" min="0" value={formData.list} onChange={handleChange}
              className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors.list ? 'border-red-500' : ''}`}
              placeholder="Enter List Price" />
            {errors.list && <p className="mt-1 text-sm text-red-600">{errors.list}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Our Cost</label>
            <input type="number" name="babs_cost" step="0.01" min="0" value={formData.babs_cost} onChange={handleChange}
              className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors.babs_cost ? 'border-red-500' : ''}`}
              placeholder="Enter Our Cost" />
            {errors.babs_cost && <p className="mt-1 text-sm text-red-600">{errors.babs_cost}</p>}
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea name="module_description" rows={3} value={formData.module_description} onChange={handleChange}
              className="w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
              placeholder="Enter Description" />
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <button type="button" onClick={onClose} className="px-8 py-2 bg-gray-300 rounded hover:bg-gray-400" disabled={saving}>Cancel</button>
          <button type="submit" className="px-8 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50" disabled={saving}>
            {saving ? 'Updating...' : 'Update'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UpdateCatalogModal;
